import React from "react";
import { useTranslation } from "react-i18next";
import { AxiosError, AxiosInstance } from "axios";
import { useMutation, useQueryClient } from "react-query";

import { useClient } from "utils/client";
import { ControlContext } from "context/Controls";

type UpdateMemberRolePayload = {
  member_email: string;
  role: string;
};

const updateMemberRole = async (
  client: AxiosInstance,
  payload: UpdateMemberRolePayload,
) => {
  try {
    const { data } = await client.put(`/accounts/members`, payload);

    return data;
  } catch (error) {
    console.error(error);
    throw new Error((error as AxiosError).response?.data);
  }
};

export const useUpdateMemberRole = () => {
  const client = useClient();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { displayNotification } = React.useContext(ControlContext);

  const { mutate, isLoading } = useMutation(
    async ({ email, role }: { email: string; role: string }) => {
      await updateMemberRole(client, { member_email: email, role });
    },
    {
      onSuccess: async () => {
        await queryClient.refetchQueries(["account"]);

        displayNotification!(
          t("notification.saving.update_member_role_success"),
          "",
        );
      },
      onError: async (e: Error) => {
        await queryClient.refetchQueries(["account"]);

        displayNotification!(e.message, "", "error");
      },
    },
  );

  return { updateRole: mutate, isLoading };
};
